import { useCallback, useEffect, useRef, useState } from 'react';
import type { ArtworkPublic, MetaPayload } from '../../shared/types';
import { fetchMeta, listImages } from '../lib/api';
import { Filters } from '../components/Filters';
import { Grid } from '../components/Grid';
import { Lightbox } from '../components/Lightbox';
import { Welcome } from '../components/Welcome';

export function GalleryPage() {
  const [items, setItems] = useState<ArtworkPublic[]>([]);
  const [meta, setMeta] = useState<MetaPayload | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagId, setTagId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Token to ignore stale responses when filters change rapidly.
  const requestToken = useRef(0);

  // Reset and reload when filters change.
  useEffect(() => {
    const token = ++requestToken.current;
    setItems([]);
    setCursor(null);
    setHasMore(true);
    setError(null);
    setLoading(true);
    listImages({ categoryId, tagId })
      .then((page) => {
        if (requestToken.current !== token) return;
        setItems(page.items);
        setCursor(page.next_cursor);
        setHasMore(!!page.next_cursor);
      })
      .catch((err: Error) => {
        if (requestToken.current !== token) return;
        setError(err.message);
      })
      .finally(() => {
        if (requestToken.current === token) setLoading(false);
      });
  }, [categoryId, tagId]);

  // Load filter metadata once on mount.
  useEffect(() => {
    fetchMeta().then(setMeta).catch(() => {/* non-fatal */});
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || !cursor) return;
    const token = requestToken.current;
    setLoading(true);
    try {
      const page = await listImages({ cursor, categoryId, tagId });
      if (requestToken.current !== token) return;
      setItems((prev) => [...prev, ...page.items]);
      setCursor(page.next_cursor);
      setHasMore(!!page.next_cursor);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      if (requestToken.current === token) setLoading(false);
    }
  }, [loading, hasMore, cursor, categoryId, tagId]);

  // Intersection observer for infinite scroll.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '600px 0px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loadMore]);

  return (
    <div className="space-y-10">
      <Welcome />

      <Filters
        meta={meta}
        selectedCategory={categoryId}
        selectedTag={tagId}
        onCategoryChange={setCategoryId}
        onTagChange={setTagId}
      />

      {error && (
        <div className="rounded-2xl border border-accent/30 bg-accent/5 p-4 text-sm text-accent">
          Couldn't load the gallery: {error}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <EmptyState filtered={!!(categoryId || tagId)} />
      )}

      <Grid
        items={items}
        onOpen={(i) => setLightboxIndex(i)}
      />

      <div ref={sentinelRef} className="h-12" />
      {loading && (
        <div className="py-6 text-center text-sm text-ink-500">Loading more…</div>
      )}
      {!hasMore && items.length > 0 && (
        <div className="py-6 text-center text-xs text-ink-500">
          You've reached the end. Come back soon for new work!
        </div>
      )}

      {lightboxIndex !== null && items[lightboxIndex] && (
        <Lightbox
          items={items}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="rounded-2xl border border-dashed border-ink-900/10 p-12 text-center">
      <div className="font-display text-2xl text-ink-700">
        {filtered ? 'Nothing here yet' : 'The gallery is just getting started'}
      </div>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-500">
        {filtered
          ? 'Try clearing the filters or picking a different category.'
          : 'New artwork will show up here as soon as the first piece is uploaded.'}
      </p>
    </div>
  );
}
